import { createContext, useContext, useEffect, useState } from 'react'
import { useMoteLoopMutation, useMoteStatusQuery } from '@mote/api'

/**
 * @typedef {Object} MoteLoopContextValue
 * @property {(moteLoopPayload: MoteLoopPayload) => void} loop
 * @property {() => ToteStatusPick} getToteStatus
 * @property {() => boolean} getIsLoopy
 * @property {() => ({affirmativeCarousel: string[], assistiveCarousel: string[]})} getCarousels
 * @property {() => void} show
 * @property {boolean} isShown
 * @property {boolean} isUnauthenticated
 */

/**
 * @type {React.Context<MoteLoopContextValue | null>}
 */
const MoteLoopContext = createContext(null)

/**
 * @typedef {Object} MoteLoopProviderProps
 * @property {string} moteId
 * @property {string[]} affirmativeCarousel
 * @property {string[]} assistiveCarousel
 * @property {JSX.Element} children
 * @property {boolean} isUnauthenticated
 */

/**
 * @param {MoteLoopProviderProps} props
 * @return {JSX.Element}
 */
export function MoteLoopProvider(props) {
  const moteStatusQuery = useMoteStatusQuery({ moteId: props.moteId })
  const moteLoopMutation = useMoteLoopMutation(props.moteId)
  const [isShown, setIsShown] = useState(false)

  useEffect(() => {
    moteStatusQuery.mutate()
  }, [props.moteId])

  return (
    <MoteLoopContext.Provider
      value={{
        loop: variables => moteLoopMutation.mutate(variables),
        getToteStatus,
        getIsLoopy,
        getCarousels,
        show: () => setIsShown(true),
        isShown: canBeShown(),
        isUnauthenticated: props.isUnauthenticated
      }}
    >
      {props.children}
    </MoteLoopContext.Provider>
  )

  /**
   * @return {boolean}
   */
  function canBeShown() {
    return isShown && hasData() && getIsLoopy()
  }

  /**
   * @return {{affirmativeCarousel: string[], assistiveCarousel: string[]}}
   */
  function getCarousels() {
    return {
      affirmativeCarousel: getDevice().affirmativeCarousel,
      assistiveCarousel: getDevice().assistiveCarousel
    }
  }

  /**
   * @return ToteStatusPick
   */
  function getToteStatus() {
    return {
      createdByAvatar: getMoteStatus().createdByAvatar,
      createdByName: getMoteStatus().createdByName,
      wasCreatedBySameUser: getMoteStatus().wasCreatedBySameUser,
      studentName: getMoteStatus().studentName,
      lastSeenEpoch: getMoteStatus().lastSeenEpoch,
      lastLoopMode: getMoteStatus().lastLoopMode,
      lastTeply: getMoteStatus().lastTeply
    }
  }

  /**
   * @return {boolean}
   */
  function getIsLoopy() {
    return getMoteStatus().isLoopy
  }

  /**
   * @return {MoteStatus}
   */
  function getMoteStatus() {
    if (!hasData()) {
      throw new Error('Data is not initialized')
    }
    return moteStatusQuery.data.moteStatus
  }

  /**
   * @return {Device}
   */
  function getDevice() {
    if (!hasData()) {
      throw new Error('Data is not initialized')
    }
    return moteStatusQuery.data.device
  }

  /**
   * @return {boolean}
   */
  function hasData() {
    return Boolean(moteStatusQuery.data)
  }
}

/**
 * @return MoteLoopContextValue | null
 */
export function useUnsafeMoteLoopContext() {
  return useContext(MoteLoopContext)
}

/**
 * @return MoteLoopContextValue
 */
export function useMoteLoopContext() {
  const value = useUnsafeMoteLoopContext()
  if (!value) {
    throw new Error('To useLoopContext you have to wrap it in MoteLoopProvider')
  }
  return value
}
